/*
Persistent cache of the last confirmed RoundAggregate.

The webapp normally re-fetches the full confirmed aggregate from the server on
every load (`getAggregate`). This module persists that aggregate in IndexedDB
so a previously-loaded round can open while the device is offline (primarily
the iPad Capacitor app). Unconfirmed actions stored in `unconfirmedActions2`
are replayed on top of the cached aggregate exactly as they are on top of a
freshly-downloaded one.

Correctness/invalidations:
  - A cached row is only served when its schemaHash, userProfileHash,
    viewerTrack, and viewerUserId all match the current session, and the stored
    aggregate still parses against the current Zod schema. Anything else is
    treated as a cache miss and the row is deleted. viewerUserId matters
    because aggregate anonymization is per-viewer, not just per-track: a
    restricted funder's hidden recommenders' notes are deleted based on WHO is
    viewing, while two same-track viewers share a userProfileHash — without the
    viewer in the key, an admin switching impersonation between them could be
    served the other viewer's note visibility from the cache.
  - All writes are best-effort: a failed cache write (quota, transient
    IndexedDB disconnect) must never break the action pipeline. Failures are
    reported to Sentry and otherwise swallowed.
*/

import {
  roundAggregateSchema,
  type RoundAggregate,
} from "@engine/round/aggregate";
import { type Track } from "@lib/types";
import { captureMessage } from "@client/telemetry/sentry";
import { type EntityTable } from "dexie";

export type RoundAggregateCacheRecord = {
  aggregateId: string;
  aggregate: RoundAggregate;
  version: number;
  cachedAt: number;
  schemaHash: string;
  userProfileHash: string;
  viewerTrack: Track;
  // The effective viewer the aggregate was anonymized for. Optional for
  // back-compat with rows written before this field existed (those mismatch on
  // read and are discarded — a one-time re-download). Non-indexed, so no Dexie
  // version bump needed.
  viewerUserId?: string | null;
};

export type RoundAggregateCacheTable = EntityTable<
  RoundAggregateCacheRecord,
  "aggregateId"
>;

export type RoundAggregateCacheKey = {
  aggregateId: string;
  schemaHash: string;
  userProfileHash: string;
  viewerTrack: Track;
  viewerUserId: string | null;
};

// Permanently stop persisting aggregates. Called on sign-out before clearing,
// so a non-blocking aggregate write can't recreate the cleared SPAStorage DB
// before the page redirects (which would leave the previous user's round cached
// for the next offline session).
let writesSuspended = false;
export function suspendAggregateCacheWrites(): void {
  writesSuspended = true;
}

// Tracks every in-flight write so sign-out can await them before deleting the
// DB. `writesSuspended` only stops writes that haven't yet passed the guard
// below; a write already inside its IndexedDB transaction would otherwise
// complete after the delete and recreate the DB with the previous user's round.
// A Set (entries removed on settle) rather than a promise chain, so a long
// offline session that persists after every action doesn't retain every prior
// settled write.
const inFlightWrites = new Set<Promise<unknown>>();
export function flushAggregateCacheWrites(): Promise<void> {
  return Promise.allSettled(inFlightWrites).then(() => {});
}

export type WriteCachedAggregateOptions = {
  /**
   * True when `aggregate` is what the server just handed this load — a fresh
   * `getAggregate` download, not a socket-advanced snapshot. Lets the guard
   * below DELETE a stored row that sits ahead of it; see the comment there.
   */
  fromFreshDownload?: boolean;
};

/** Best-effort: failures are logged to Sentry and swallowed. */
export async function writeCachedAggregate(
  table: RoundAggregateCacheTable,
  key: RoundAggregateCacheKey,
  aggregate: RoundAggregate,
  options: WriteCachedAggregateOptions = {},
): Promise<void> {
  if (writesSuspended) return;
  const record: RoundAggregateCacheRecord = {
    aggregateId: key.aggregateId,
    aggregate,
    version: aggregate.version,
    cachedAt: Date.now(),
    schemaHash: key.schemaHash,
    userProfileHash: key.userProfileHash,
    viewerTrack: key.viewerTrack,
    viewerUserId: key.viewerUserId,
  };
  const work = (async () => {
    try {
      // Read-then-write in one rw transaction so the version check is atomic: a
      // lagging writer (e.g. a second tab sharing this IndexedDB) must not
      // overwrite a newer cached snapshot with an older one. On the iPad shell the
      // cache is single-WebView so this can't actually race, but the guard keeps
      // the cache correct under any consumer and costs ~one indexed read.
      await table.db.transaction("rw", table, () =>
        table.get(key.aggregateId).then((existing) => {
          // Keep this as a Dexie promise chain instead of an `async` callback.
          // Dexie can commit a native IndexedDB transaction before a transpiled
          // async callback settles, which surfaces as PrematureCommitError.
          // Skip if the stored row is from a strictly newer aggregate version,
          // OR — at the SAME version — from a more recently *initiated* write
          // (`cachedAt`). Writes are non-blocking, so an in-flight write from an
          // older adapter (e.g. before an impersonation / viewer-track switch)
          // could land after a newer adapter wrote its row; the version is
          // unchanged but its key (userProfileHash/viewerTrack) is stale. Without
          // the cachedAt tiebreak it would clobber the current session's row, and
          // readCachedAggregate would then miss → the round shows unavailable
          // offline despite having just been loaded.
          //
          // One exception, and it DELETES rather than overwrites: a row ahead of
          // an aggregate the server just handed us describes history the server
          // doesn't have. That happens when the action log is replaced underneath
          // a browser — staging's nightly reset from production, a DB restore, a
          // round import. Kept, such a row survives forever (no later write can
          // reach a version the server never produces), costing the round its
          // cache-first fast path and, on the iPad, serving pre-reset content
          // offline. Deleting is deliberately blunt: we make no attempt to tell
          // that apart from a second tab that confirmed an action mid-load, and
          // do NOT write our own aggregate over it — dropping the row can only
          // cost a re-download, whereas overwriting it would let an older
          // aggregate be served as if it were current.
          if (existing != null && existing.version > aggregate.version) {
            if (!options.fromFreshDownload) return;
            return table.delete(key.aggregateId).then(() => undefined);
          }
          if (
            existing != null &&
            existing.version === aggregate.version &&
            existing.cachedAt > record.cachedAt
          ) {
            return;
          }
          return table.put(record);
        }),
      );
    } catch (error) {
      captureMessage(
        `Failed to persist round aggregate cache for ${
          key.aggregateId
        }: ${String(error)}`,
        { level: "warning" },
      );
    }
  })();
  // Track this write so sign-out can await it before deleting the DB; remove it
  // on settle so the set doesn't grow across a long offline session.
  inFlightWrites.add(work);
  void work.finally(() => inFlightWrites.delete(work));
  await work;
}

/**
 * Returns the cached record when it is valid for the given session key, or
 * null (deleting the stale/corrupt row) otherwise.
 */
export async function readCachedAggregate(
  table: RoundAggregateCacheTable,
  key: RoundAggregateCacheKey,
): Promise<RoundAggregateCacheRecord | null> {
  let record: RoundAggregateCacheRecord | undefined;
  try {
    record = await table.get(key.aggregateId);
  } catch (error) {
    captureMessage(
      `Failed to read round aggregate cache for ${key.aggregateId}: ${String(
        error,
      )}`,
      { level: "warning" },
    );
    return null;
  }
  if (record == null) {
    return null;
  }

  if (
    record.schemaHash !== key.schemaHash ||
    record.userProfileHash !== key.userProfileHash ||
    record.viewerTrack !== key.viewerTrack ||
    // Pre-viewerUserId rows have `undefined` here and so always mismatch —
    // deliberate: they can't prove which viewer they were anonymized for.
    record.viewerUserId !== key.viewerUserId
  ) {
    await deleteCachedAggregate(table, key.aggregateId);
    return null;
  }

  const parsed = roundAggregateSchema.safeParse(record.aggregate);
  if (!parsed.success) {
    captureMessage(
      `Cached round aggregate for ${key.aggregateId} failed schema validation; discarding`,
      { level: "warning" },
    );
    await deleteCachedAggregate(table, key.aggregateId);
    return null;
  }

  return { ...record, aggregate: parsed.data };
}

/** Best-effort: failures are swallowed (the row will be overwritten on the next online load). */
export async function deleteCachedAggregate(
  table: RoundAggregateCacheTable,
  aggregateId: string,
): Promise<void> {
  try {
    await table.delete(aggregateId);
  } catch {
    // Best-effort; a stale row is harmless because reads re-validate.
  }
}
